import {
  useContext, useMemo, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import isChatroomDisabled from 'components/chat/utils/isChatroomDisabled';
import { UserSessionContext } from 'components/UserSession';
import { selectLocale } from 'models/localization';
import regexUtil from 'utils/regexUtil';
import { isTwilioConnected } from 'models/twilio';
import useTwilioEventFunctions from 'components/twilio/hooks/useTwilioEventFunctions';

const mapAllName = (item: any, mapper: any) => {
  const cloned = { ...item };
  if (cloned.pages) {
    cloned.pages = cloned.pages.map((page) => mapAllName(page, mapper));
  }
  ['title', 'buttonName']
    .filter((field) => cloned[field])
    .forEach((field) => {
      cloned[field] = Object.fromEntries(
        Object.entries(cloned[field])
          .map(([key, value]) => [key, mapper(value)]),
      );
    });
  return cloned;
};

const useMenuItems = () => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const twilioConnected = useSelector(isTwilioConnected);
  const { joinPublicChatroom } = useTwilioEventFunctions();

  const { canSee, userSession } = useContext(UserSessionContext);
  const {
    features,
    pageNames = {},
    chatroomConfig = {},
    hall: hallConfig = {},
    customPage,
    appBarConfig = {},
    tabRegex,
  } = useSelector((state: any) => state.eventConfigs);

  const handleClick = useCallback((key, info = '') => {
    window.trackingEventToFirebase(userSession, 'MainMenu_Click', key, info);
  }, [userSession]);

  const tabNameRegex = useMemo(() => regexUtil(tabRegex), [tabRegex]);
  const getLabel = useCallback((title) => {
    if (features.includes('bilingualTab') && !!tabNameRegex) {
      const matched = title?.match(tabNameRegex);
      if (!matched) return title;
      return `${matched[1].trim()}<br/>${matched[2].trim()}`;
    }
    return title;
  }, [features, tabNameRegex]);
  const hasSinglePublicChat = chatroomConfig?.publicChatrooms?.length === 1;

  const defaultMenuItems = useMemo(() => (
    [
      {
        display: 'TOPBAR',
        name: 'lobby',
        pages: [
          {
            name: 'lobby-1',
            buttonName: { [locale]: t('page.lobby', 'Lobby'), ...(pageNames.lobby || {}) },
            path: { [locale]: '/main-lobby' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Lobby'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('lobby'),
      },
      {
        display: 'TOPBAR',
        name: 'hall',
        pages: [
          {
            name: 'hall-1',
            buttonName: { [locale]: t('page.hall', 'Exhibition Hall'), ...(pageNames.hall || {}) },
            path: { [locale]: '/hall' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Hall'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('hall') && !(features.includes('multipleHall') && hallConfig?.multipleHall),
      },
      {
        display: 'TOPBAR',
        name: 'networking-lounge',
        pages: [
          {
            name: 'networking-lounge',
            buttonName: { [locale]: t('page.networking_lounge', 'Networking Lounge'), ...(pageNames.networkingLounge || {}) },
            path: { en: '/networking-lounge' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Networking Lounge'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('networkingLounge') && features.includes('networkingLounge'),
      },
      {
        display: 'DROPDOWN',
        name: 'hall',
        title: { [locale]: t('page.hall', 'Exhibition Hall'), ...(pageNames.hall || {}) },
        pages: (hallConfig.multipleHall || [])
          .map(({ redirectUrl, label, hallName: subHallName }) => ({
            name: subHallName,
            buttonName: label,
            path: Object.fromEntries(
              Object.entries(redirectUrl || {})
                .map(([key, value]) => [key, `${value}${subHallName ? `&hallName=${subHallName}` : ''}`]),
            ),
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Hall', subHallName),
            preventDefaultTracking: true,
          })),
        show: canSee('hall') && !!(features.includes('multipleHall') && hallConfig?.multipleHall),
      },
      {
        display: 'TOPBAR',
        name: 'webinar',
        pages: [
          {
            name: 'webinar-1',
            buttonName: { [locale]: t('page.webinar', 'Webinars'), ...(pageNames.webinar || {}) },
            path: { [locale]: '/webinar' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Webinar'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('webinar'),
      },
      {
        display: 'TOPBAR',
        name: 'speakers',
        pages: [
          {
            name: 'speakers-1',
            buttonName: { [locale]: t('page.speakers', 'Speakers'), ...(pageNames.speakers || {}) },
            path: { [locale]: '/speakers' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Speakers'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('webinar') && canSee('speakers'),
      },
      {
        display: 'TOPBAR',
        name: 'participants',
        pages: [
          {
            name: 'participants-1',
            buttonName: { [locale]: t('page.participants', 'Participants'), ...(pageNames.participant || {}) },
            path: { [locale]: '/participant' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Participant'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('participant'),
      },
      {
        display: hasSinglePublicChat ? 'TOPBAR' : 'DROPDOWN',
        name: 'public-chat',
        title: { [locale]: t('page.publicChatroom', 'Public Chatroom'), ...(pageNames['public-chat'] || {}) },
        pages: (chatroomConfig.publicChatrooms || [])
          .map(({ id, label }) => {
            let buttonName: {
              [locale: string]: string;
            };
            if (typeof label === 'object') {
              buttonName = { ...buttonName, ...label };
            } else {
              buttonName = { [locale]: label || t('page.publicChatroom', 'Networking Lounge') };
            }
            return ({
              name: id,
              pageTitle: id,
              buttonName,
              path: { [locale]: '#' },
              action: { desktop: 'INLINE' },
              onClick: () => {
                if (twilioConnected) joinPublicChatroom(id);
              },
              preventDefaultTracking: true,
            });
          }).filter(() => twilioConnected),
        show: (
          canSee('publicChatroom')
          && !isChatroomDisabled(chatroomConfig)
          && chatroomConfig.publicChatrooms?.length
        ),
      },
      {
        display: 'TOPBAR',
        name: 'products',
        pages: [
          {
            name: 'products-1',
            buttonName: { [locale]: t('page.products', 'Products'), ...(pageNames.products || {}) },
            path: { [locale]: '/products' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Product Page'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('products'),
      },
      {
        display: 'TOPBAR',
        name: 'custom',
        pages: [
          {
            name: 'custom-1',
            buttonName: { [locale]: t('page.custom', 'Custom Page'), ...(pageNames.custom || {}) },
            path: { [locale]: '/custom' },
            action: { desktop: 'INLINE' },
            onClick: () => handleClick('Custom Page'),
            preventDefaultTracking: true,
          },
        ],
        show: canSee('lobby') && features.includes('customPage'),
      },
    ].filter((it) => it.show)
  ), [
    canSee,
    chatroomConfig,
    features,
    hallConfig,
    handleClick,
    locale,
    pageNames,
    t,
    hasSinglePublicChat,
    twilioConnected,
    joinPublicChatroom,
  ]);

  const validMenuItems = useMemo(
    () => {
      const customPageList = [...defaultMenuItems];
      if (features.includes('multipleCustomPages') && canSee('lobby') && customPage) {
        customPageList.push(...customPage);
      }
      return customPageList
        .filter(({ display }) => ['TOPBAR', 'DROPDOWN'].includes(display))
        .map((it) => mapAllName(it, getLabel))
        .sort((prev, next) => (
          (appBarConfig?.itemOrder?.[prev.name] ?? Number.MAX_SAFE_INTEGER)
          - (appBarConfig?.itemOrder?.[next.name] ?? Number.MAX_SAFE_INTEGER)
        ));
    },
    [appBarConfig?.itemOrder, canSee, customPage, defaultMenuItems, features, getLabel],
  );

  return validMenuItems;
};

export default useMenuItems;
